import palettesJson from './palettes.json' with { type: 'json' }

export * from './oklch.ts'

export type ThemeMode = 'light' | 'dark'
export type TokenName =
  | '--surface-sunken' | '--surface' | '--surface-raised' | '--surface-overlay' | '--surface-inset'
  | '--text' | '--text-muted' | '--text-faint' | '--text-disabled'
  | '--line' | '--line-strong'
  | '--accent' | '--accent-text' | '--accent-on' | '--accent-quiet'
  | '--row-hover' | '--row-cursor' | '--row-selected' | '--unread-bar' | '--focus-ring'

export interface Palette {
  readonly tagline: string
  readonly light: Readonly<Record<TokenName, string>>
  readonly dark: Readonly<Record<TokenName, string>>
}

/**
 * Danger and warning, which are NOT palette tokens.
 *
 * THEY WERE USED BEFORE THEY EXISTED. `shell.css` has referenced
 * `var(--sem-danger)` and `var(--sem-warn)` since it was
 * written, and neither name was ever defined anywhere. An undefined custom
 * property makes the whole declaration invalid, so `.modal-destructive` — the
 * last-chance confirm for deleting tens of thousands of messages — was
 * `color: var(--surface)` on no background at all: the label of the most
 * dangerous button in the client, in the surface colour, on the surface.
 *
 * ONE PAIR ACROSS ALL FIVE PALETTES, and that is the decision rather than an
 * economy. `palettes.json` is a mood — Ledger's warm greys, Phosphor's green —
 * and "this will destroy something" is not a mood; a household that picks a
 * calmer palette must not get a calmer warning. Red and amber mean the same
 * thing in every one of them, so they are stated once here instead of five
 * times there, where the fifth would be the one that drifts.
 *
 * The values are pinned by `test/semantic.test.ts`, which checks them against
 * EVERY palette's surfaces in both themes — the surface a palette chooses is
 * exactly what these have to stay legible on.
 */
export type SemanticName = '--sem-danger' | '--sem-warn'

export const SEMANTICS: Readonly<Record<ThemeMode, Readonly<Record<SemanticName, string>>>> = {
  light: {
    // Dark enough to carry AA body text on the lightest surface any palette has
    // AND to take the surface colour as a label when it is a button ground —
    // which is the same contrast pair, so one number settles both.
    '--sem-danger': 'oklch(50% 0.18 27)',
    // Amber reads as brown once it is dark enough for AA on white. That is the
    // cost of using it for TEXT (`.push-state`), and it is the right trade:
    // legible beats vivid for a line that only appears when something is wrong.
    '--sem-warn': 'oklch(50% 0.1 70)',
  },
  dark: {
    '--sem-danger': 'oklch(66% 0.14 27)',
    '--sem-warn': 'oklch(64% 0.12 75)',
  },
}

export const palettes = palettesJson as unknown as Readonly<Record<string, Palette>>
export const paletteNames = Object.keys(palettes)

/** The palette that ships as the default. Quiet enough to live in all day. */
export const DEFAULT_PALETTE = 'Ledger'

/**
 * Contrast floors the design commits to. These are asserted in tests, so a
 * palette edit that breaks accessibility fails the build rather than shipping.
 */
export const FLOORS = {
  /** Body text on its surface (WCAG AA normal text). */
  bodyText: 4.5,
  /** Secondary text and large text (WCAG AA large). */
  secondaryText: 4.5,
  /** Non-text indicators that identify a component or state (WCAG 1.4.11). */
  indicator: 3.0,
  /**
   * Decorative hairlines (list row separators). Exempt from 1.4.11 because the
   * rows are also separated by spacing and content, but they must not vanish.
   */
  hairline: 1.2,
  /** Row-state tints are perceptual steps, not contrast; they need a minimum ΔL. */
  rowStateDeltaL: 0.015,
} as const
